import { useEffect, useMemo, useState } from "react"
import { attributeMapping } from "../../utils/programmaticUtils"
import { ItemSummary, LayerItemAttribute } from "../../services/types"
import { formatValue, parseMarkdown } from "../../utils/helpers"

// Section Images
import waterSvg from '../../assets/credc/Water.svg';
import transportationSvg from '../../assets/credc/Transportation.svg';
import sanitarySvg from '../../assets/credc/Sanitary Sewer.svg';
import powerSvg from '../../assets/credc/Power Network.svg';
import generalSvg from '../../assets/credc/General.svg';
import fundamentalsSvg from '../../assets/credc/Fundamentals.svg';
import broadbandSvg from '../../assets/credc/Broadband.svg';
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";


interface Attribute {
  name: string
  value: string
}

interface MapItemMatrixUIProps {
  name: string;
  description?: string;
  category?: string;
  value?: number;
  lat?: number;
  long?: number;
  attributes: LayerItemAttribute[];
  summary: ItemSummary | undefined;
  onClose?: () => void;
}

interface CollapsibleSectionProps {
  title: string;
  attributes: Attribute[];
  isExpanded: boolean;
  onToggle: () => void;
}

const getCategoryColor = (category?: string) => {
    if (!category) return; 

    switch (category?.toLowerCase()) {
        case "near term":
            return "bg-[#A3C9A8] text-[#1B4332]";
        case "mid term":
            return "bg-[#A0BFE0] text-[#102A43]";
        case "near term - unlikely to convert":
            return "bg-[#FFE29A] text-[#5F370E]";
        case "long term":
            return "bg-[#CA9DFF] text-[#49276F]";
        default:
            return "bg-gray-600 text-white";
    };
};

function CollapsibleSection({ title, attributes, isExpanded, onToggle }: CollapsibleSectionProps) {
    const scoreMap: Record<string, { label: string; max: number }> = {
        "Fundamentals": { label: "Fundamentals Score", max: 53 },
        "Transportation": { label: "Transportation Score", max: 24 },
        "Power Network": { label: "Power Network Score", max: 6 },
        "Broadband": { label: "Broadband Score", max: 5 },
        "Water": { label: "Water Score", max: 6 },
        "Sanitary Sewer": { label: "Sanitary Sewer Score", max: 6 },
        "General": { label: "", max: 0 } // no score
    };

    const scoreValue = useMemo(() => {
        const config = scoreMap[title];
        
        if (!config?.label) return null; // No score for General
        const scoreAttr = attributes.find(attr => attr.name === config.label);
        return scoreAttr ? `${scoreAttr.value} out of ${config.max}` : null;
    }, [title, attributes]);

    const svgMap: Record<string, string> = {
        General: generalSvg,
        Fundamentals: fundamentalsSvg,
        Transportation: transportationSvg,
        "Power Network": powerSvg,
        Broadband: broadbandSvg,
        Water: waterSvg,
        "Sanitary Sewer": sanitarySvg,
    }

    return (
        <div className="border-b border-gray-300">
        <button
            onClick={onToggle}
            className="w-full p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-start space-x-2">
                    <img src={svgMap[title]} className="w-5 h-5"></img>
                    <div className="flex flex-col text-left">
                        <span className="font-semibold text-sm uppercase tracking-wide">{title}</span>
                        {scoreValue && (
                            <span className="text-xs">
                                Score: {scoreValue}
                            </span>
                        )}
                    </div>
                </div>
                <span>{isExpanded ? "−" : "+"}</span>
            </div>
        </button>

        {isExpanded && (
            <div className="bg-white">
            {attributes
                .filter(attr => !attr.name.toLowerCase().includes("score"))
                .map((attr, index) => (
                <div key={index} className="grid grid-cols-2 border-b border-gray-200 last:border-b-0">
                <div className="p-3 bg-gray-50 border-r border-gray-200">
                    <span className="text-xs font-medium">{attr.name}</span>
                </div>
                <div className="p-3">
                    <span className='text-xs' dangerouslySetInnerHTML={{ __html: parseMarkdown(attr.value) }}></span>
                </div>
                </div>
            ))}
            </div>  
        )}
        </div>  
    )
}

export default function MapItemMatrixUI({
  name,
  description,
  category,
  value,
  lat,
  long,
  attributes,
  summary,
  onClose,
}: MapItemMatrixUIProps) {
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        General: false,
        Core: false,
        Transportation: false,
        "Power Network": false,
        Broadband: false,
        Water: false,
        "Sanitary Sewer": false,
    });
    const [streetViewLoaded, setStreetViewLoaded] = useState(false);
    const [streetViewSrc, setStreetViewSrc] = useState<string | null>(null);

    useEffect(() => {
        if (lat && long) {
            const url = `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getstreetview?lat=${lat}&lng=${long}`;
            const img = new Image();
            img.src = url;
            img.onload = () => {
            setStreetViewSrc(url);
            setStreetViewLoaded(true);
            };
        }
    }, [lat, long]);

    const toggleSection = (sectionName: string) => {
        setExpandedSections((prev) => ({
        ...prev,
        [sectionName]: !prev[sectionName],
        }))
    }
    console.log('attributes:', attributes);

    const groupedAttributes = Object.entries(attributeMapping)
        .map(([sectionName, attributeNames]) => {
        const sectionAttributes = attributeNames
            .map((attrName) => {
            const attr = attributes.find((a) => a.name === attrName)
            return {
                name: attrName,
                value: attr?.value || "—",
            }
            })
            .filter((a) => a.value !== "—")
        return {
            sectionName,
            attributes: sectionAttributes,
        }
        })
        .filter((s) => s.attributes.length > 0);

    const overallScoreAttr = attributes.find(attr => attr.name === "Overall Score");

    return (
        <div className="max-w-2xl mx-auto bg-white border border-t-0 border-gray-300 shadow-lg">
            <div className={`flex items-center justify-between p-4 border-b ${getCategoryColor(category)}`}>
                <h1 className="text-xl font-bold uppercase tracking-wide">{name}</h1>
                {onClose && (
                <button onClick={onClose} className="text-gray-600 text-lg font-bold hover:text-black">
                    ×
                </button>
                )}
            </div>

            {streetViewSrc && streetViewLoaded 
            ? (
                <img
                    src={streetViewSrc}
                    alt="Site map"
                    style={{
                    width: '100%',
                    maxHeight: '20rem',
                    }}
                />
            )
            :
            <Skeleton height={200} />
            }

            {description && (
                <div className="p-4 border-b border-gray-300">
                    <p className='text-xs' dangerouslySetInnerHTML={{ __html: parseMarkdown(description) }}></p>
                </div>
            )}

            {category && (
                <div className="grid grid-cols-2 border-b border-gray-300">
                    <div className="p-3 flex items-center border-r border-gray-300 font-semibold text-xs">Category</div>
                    <div className="p-3 text-xs">{category}</div>
                </div>
            )}

            {value !== undefined && (
                <div className="grid grid-cols-2 border-b border-gray-300">
                    <div className="p-3 border-r border-gray-300 font-semibold text-xs">Gross Acres:</div>
                    <div className="p-3 text-xs">
                        {formatValue(value, summary?.unit ?? 'integer')} <span className="text-[10px]">{summary?.unit_descriptor || 'gross acres'}</span>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-2 border-b border-gray-300">
                <div className="p-3 border-r border-gray-300 font-semibold text-xs">Overall Score:</div>
                <div className="p-3 text-xs">
                    {overallScoreAttr?.value ?? "—"} <span className="text-[10px]">out of 100</span>
                </div>
            </div>

            <div>
                {groupedAttributes.map(({ sectionName, attributes }) => (
                <CollapsibleSection
                    key={sectionName}
                    title={sectionName}
                    attributes={attributes}
                    isExpanded={expandedSections[sectionName]}
                    onToggle={() => toggleSection(sectionName)}
                />
                ))}
            </div>
        </div>
    )
}
